/**
 * governance/cognitiveLevel.ts
 * Cognitive level detection for questions and explanations.
 *
 * Maps questions to Bloom's taxonomy levels (simplified 4-level model):
 * - recall:        Reproduce facts, definitions, procedures
 * - comprehension: Explain meaning, interpret, paraphrase
 * - application:   Apply formula/rule to a new situation, calculate
 * - analysis:      Compare, contrast, decompose, reason about structure
 * - unknown:       Pattern-detector could not classify (Ruis-audit v1.6 T2.1)
 *
 * NOTE (Ruis-audit v1.6 §25 T2.1 / C6 fix):
 * Default previously returned 'recall' when no pattern matched, which silently
 * mis-classified short or atypical questions as low-Bloom. Now returns
 * 'unknown' so downstream consumers can decide to emit telemetry or skip
 * Bloom-gated checks. Callers must handle 'unknown' explicitly — see
 * contentValidators/assessmentChecks.ts for the Plus-tier handling.
 */

import type { Question } from '../content/types'

export type CognitiveLevel =
  | 'recall'
  | 'comprehension'
  | 'application'
  | 'analysis'
  | 'unknown'

// ─── Detection patterns ───────────────────────────────────────────────────────

/** Analysis-level indicators: comparison, reasoning, decomposition, error analysis, reverse reasoning */
const ANALYSIS_PATTERNS =
  /vergelijk|verschil|waarom|analyseer|verklaar|redeneer|bewijs|leg uit waarom|welke conclusie|wat is de oorzaak|wat klopt er niet|wat is de fout|wat is zijn fout|welke stap mist|wat doet.{0,15}(?:fout|verkeerd)|corrigeer|beoordeel|reken\s*terug|terugrekenen|oorspronkelijke\s+(?:prijs|bedrag|waarde)|vóór\s+de\s+(?:korting|verhoging|actie|afprijzing)|waar\s+of\s+onwaar|juist\s+of\s+onjuist|in\s+de\s+juiste\s+volgorde/i

/** Application-level indicators: calculation, formula use, step execution */
const APPLICATION_PATTERNS =
  /\d+\s*[×x\*÷\/\+\-]\s*\d+|=\s*\d|formule|bereken|stap|eerst.*dan|vervolgens|oplossen|uitrekenen|toepassen/i

/** Comprehension-level indicators: meaning, explanation, interpretation */
const COMPREHENSION_PATTERNS =
  /betekent|want|omdat|uitleg|verklaring|bedoelt|beschrijf|wat is het verschil|hoe werkt|wat begrijp/i

/** Recall-level indicators: name, list, define, identify */
const RECALL_PATTERNS =
  /wat is|welke|noem|geef|schrijf op|hoeveel|wanneer|wie|waar|definitie|omschrijf/i

// ─── Main detection function ──────────────────────────────────────────────────

/**
 * Detects the cognitive level of a question based on its text and explanation.
 * Uses a priority cascade: analysis > application > comprehension > recall.
 *
 * Returns 'unknown' when no pattern matches — callers should treat this as
 * "needs manual classification" rather than silently assuming recall.
 */
export function detectCognitiveLevel(question: Question): CognitiveLevel {
  const questionText = question.question ?? ''
  const explanationText = question.feedback?.explanationText ?? ''
  const combined = `${questionText} ${explanationText}`.trim()

  // Empty input → unknown (cannot classify)
  if (!combined) return 'unknown'

  // Priority cascade — highest level wins
  if (ANALYSIS_PATTERNS.test(combined)) return 'analysis'
  if (APPLICATION_PATTERNS.test(combined)) return 'application'
  if (COMPREHENSION_PATTERNS.test(combined)) return 'comprehension'
  if (RECALL_PATTERNS.test(combined)) return 'recall'

  // No pattern matched — explicit unknown rather than silent 'recall' default.
  // Ruis-audit v1.6 T2.1: prevents false-positive "recall" tags on short or
  // atypical questions and lets downstream gates emit telemetry.
  return 'unknown'
}

// NOTE: `cognitiveLevelFromDok(dok)` was removed in RC-3 (G-14, audit 2026)
// as a dead export. The bridge mapping (DOK→CognitiveLevel) is trivial; if a
// future admin-display consumer needs it, recreate from the table in
// docs/audits/GOVERNANCE_DOMAIN_RC_AUDIT_2026.md §11.
//
// NOTE: stab-ronde 2 (mei 2026) — `cognitiveLevelLabel`, `cognitiveLevelColor`,
// en `computeCognitiveLevelDistribution` zijn verwijderd: nul consumers in src/.
// Voor admin-display: hercreëer een trivial mapping in de UI-laag zelf.
